import React from 'react';
import {
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { DocumentType } from '../types/request';
import { useRequestForm } from '../hooks/useRequestForm';

interface RequestFormProps {
  onSuccess: () => Promise<void>;
  setSuccess: (message: string | null) => void;
  setError: (message: string | null) => void;
}

const RequestForm: React.FC<RequestFormProps> = ({ onSuccess, setSuccess, setError }) => {
  const {
    documentType,
    setDocumentType,
    metadata,
    updateMetadata,
    aiOnly,
    setAiOnly,
    loading,
    handleSubmit
  } = useRequestForm(onSuccess, setSuccess, setError);

  const handleDataPointsChange = (value: string) => {
    updateMetadata({ data_points: value.split(',')});
  };

  return (
    <Paper sx={{ p: 3 }}>
      <form onSubmit={handleSubmit}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="document-type-label">Document Type</InputLabel>
          <Select
            labelId="document-type-label"
            value={documentType}
            label="Document Type"
            onChange={(e) => {
              setDocumentType(e.target.value as DocumentType);
              updateMetadata({}); // Reset metadata when changing document type
            }}
          >
            <MenuItem value="pdf">PDF</MenuItem>
            <MenuItem value="sec">SEC Filing</MenuItem>
            <MenuItem value="credit_card">Credit Card</MenuItem>
            <MenuItem value="earnings_search">Earnings Search</MenuItem>
          </Select>
        </FormControl>

        {documentType === 'pdf' && (
          <>
            <TextField
              fullWidth
              label="PDF URL"
              sx={{ mb: 2 }}
              onChange={(e) => updateMetadata({ url: e.target.value })}
              value={metadata.url || ''}
              helperText="Enter the URL of the PDF document"
            />
            <TextField
              fullWidth
              label="Data Points"
              sx={{ mb: 2 }}
              onChange={(e) => handleDataPointsChange(e.target.value)}
              value={metadata.data_points || ''}
              helperText="Enter comma-separated list of data points to extract"
            />
          </>
        )}

        {documentType === 'sec' && (
          <>
            <TextField
              fullWidth
              label="Stock Ticker"
              sx={{ mb: 2 }}
              onChange={(e) => updateMetadata({ ticker: e.target.value })}
              value={metadata.ticker || ''}
              helperText="Enter the stock ticker symbol"
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="filing-type-label">Filing Type</InputLabel>
              <Select
                labelId="filing-type-label"
                value={metadata.filing_type || ''}
                label="Filing Type"
                onChange={(e) => updateMetadata({ 
                  filing_type: e.target.value as '10-K' | '10-Q' | '8-K' 
                })}
              >
                <MenuItem value="10-K">10-K</MenuItem>
                <MenuItem value="10-Q">10-Q</MenuItem>
                <MenuItem value="8-K">8-K</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Fiscal Period"
              sx={{ mb: 2 }}
              onChange={(e) => updateMetadata({ fiscal_period: e.target.value })}
              value={metadata.fiscal_period || ''}
              helperText="Enter the fiscal period (e.g., FY2024, 2Q2023)"
            />
            <TextField
              fullWidth
              label="Financial Metrics"
              sx={{ mb: 2 }}
              onChange={(e) => handleDataPointsChange(e.target.value)}
              value={metadata.data_points || ''}
              helperText="Enter comma-separated list of financial metrics to extract"
            />
          </>
        )}

        {documentType === 'credit_card' && (
          <TextField
            fullWidth
            label="Transaction Data"
            multiline
            rows={4}
            sx={{ mb: 2 }}
            onChange={(e) => updateMetadata({ transaction: e.target.value })}
            value={metadata.transaction || ''}
            helperText="Enter the transaction data to analyze"
          />
        )}

        {documentType === 'earnings_search' && (
          <>
            <TextField
              fullWidth
              label="Company Name"
              sx={{ mb: 2 }}
              onChange={(e) => updateMetadata({ company: e.target.value })}
              value={metadata.company || ''}
              helperText="Enter the company name"
            />
            <TextField
              fullWidth
              label="Quarter (optional)"
              sx={{ mb: 2 }}
              onChange={(e) => updateMetadata({ quarter: e.target.value })}
              value={metadata.quarter || ''}
              helperText="Enter the quarter (e.g., Q1, Q2)"
            />
            <TextField
              fullWidth
              label="Year (optional)"
              sx={{ mb: 2 }}
              onChange={(e) => updateMetadata({ year: e.target.value })}
              value={metadata.year || ''}
              helperText="Enter the year (e.g., 2023)"
            />
          </>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={aiOnly}
              onChange={(e) => setAiOnly(e.target.checked)}
            />
          }
          label="AI-only mode (skip human review)"
          sx={{ mb: 2, display: 'block' }}
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Submit'}
        </Button>
      </form>
    </Paper>
  );
};

export default RequestForm;
